import React, { useState } from "react";
import { Typography, Box, Grid, Button, Collapse } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { colors } from "../../Theme/theme";

const ProfileImageWrapper = styled(Box)(({ theme }) => ({
	position: "relative",
	borderRadius: "0",
	overflow: "hidden",
	boxShadow: "0 12px 32px rgba(0, 0, 0, 0.25)",
	height: "320px",
	width: "100%",
	background: `
		radial-gradient(ellipse at 110% -10%, rgba(162, 192, 255, 0.18) 0%, transparent 55%),
		linear-gradient(165deg, ${colors.primary.dark} 0%, ${colors.primary.darker} 55%, #071a3e 100%)
	`,
	"&::after": {
		content: '""',
		position: "absolute",
		left: 0,
		top: 0,
		width: "5px",
		height: "100%",
		background: `linear-gradient(to bottom, ${colors.accent.main}, ${colors.primary.main})`,
		zIndex: 3,
	},
	[theme.breakpoints.down("md")]: {
		height: "260px",
		maxWidth: "280px",
		margin: "0 auto",
	},
}));

const ProfileImg = styled("img")(() => ({
	width: "100%",
	height: "100%",
	objectFit: "cover",
	objectPosition: "top center", // overridden per-member via style prop
	transition: "transform 0.5s ease",
	position: "relative",
	zIndex: 2,
	"&:hover": {
		transform: "scale(1.03)",
	},
}));

const MemberTitle = styled(Typography)(({ theme }) => ({
	marginBottom: theme.spacing(1),
	color: colors.primary.main,
	fontWeight: 700,
	fontSize: "clamp(1.6rem, 2vw, 2.2rem)",
	letterSpacing: "0.02em",
	fontFamily: "Georgia, 'Times New Roman', serif",
	lineHeight: 1.2,
	[theme.breakpoints.down("md")]: {
		textAlign: "center",
	},
}));

const MemberRole = styled(Typography)(({ theme }) => ({
	fontSize: "0.78rem",
	fontWeight: 700,
	letterSpacing: "0.18em",
	color: colors.primary.main,
	textTransform: "uppercase",
	fontFamily: "'Heebo', sans-serif",
	marginBottom: theme.spacing(2),
	[theme.breakpoints.down("md")]: {
		textAlign: "center",
	},
}));

const RoleDivider = styled(Box)(() => ({
	width: "48px",
	height: "3px",
	background: `linear-gradient(90deg, ${colors.accent.main}, ${colors.accent.light})`,
	marginBottom: "20px",
}));

const ContentText = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	lineHeight: 1.8,
	color: "#4a5568",
	fontFamily: "'Heebo', sans-serif",
	[theme.breakpoints.down("md")]: {
		textAlign: "center",
	},
}));

const TeamMember = ({ member, index, inView, isReversed = false, imagePosition = "top center" }) => {
	const [expanded, setExpanded] = useState(false);
	const animationDelay = index * 0.2;
	const imgPosition = member.imagePosition || imagePosition;
	const hasMoreParagraphs = member.bio.length > 1;

	const imageColumn = (
		<Grid item xs={12} md={3} sx={{ order: { xs: 1, md: isReversed ? 2 : 1 } }}>
			<motion.div
				initial={{ opacity: 0, x: isReversed ? 40 : -40 }}
				animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: isReversed ? 40 : -40 }}
				transition={{ duration: 0.8, delay: animationDelay, ease: "easeOut" }}
			>
				<ProfileImageWrapper>
					<ProfileImg src={member.image} alt={`${member.name}, ${member.title}`} loading="lazy" style={{ objectPosition: imgPosition }} />
					<Box
						sx={{
							position: "absolute",
							bottom: 0,
							left: 0,
							right: 0,
							zIndex: 3,
							background: "linear-gradient(to top, rgba(10, 36, 72, 0.92) 0%, transparent 100%)",
							px: 2,
							pt: 4,
							pb: 1.5,
						}}
					>
						<Typography
							sx={{
								fontFamily: "Georgia, 'Times New Roman', serif",
								fontWeight: 700,
								fontSize: "0.95rem",
								color: "white",
								lineHeight: 1.2,
							}}
						>
							{member.name}
						</Typography>
						<Typography
							sx={{
								fontSize: "0.72rem",
								fontWeight: 600,
								letterSpacing: "0.12em",
								textTransform: "uppercase",
								color: colors.accent.main,
								fontFamily: "'Heebo', sans-serif",
							}}
						>
							{member.title}
						</Typography>
					</Box>
				</ProfileImageWrapper>
			</motion.div>
		</Grid>
	);

	const textColumn = (
		<Grid item xs={12} md={9} sx={{ order: { xs: 2, md: isReversed ? 1 : 2 } }}>
			<motion.div
				initial={{ opacity: 0, y: 24 }}
				animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
				transition={{ duration: 0.8, delay: animationDelay + 0.15, ease: "easeOut" }}
			>
				<MemberRole>{member.title}</MemberRole>
				<MemberTitle>{member.name}</MemberTitle>
				<RoleDivider />

				<ContentText paragraph>{member.bio[0]}</ContentText>

				{hasMoreParagraphs && (
					<>
						<Collapse in={expanded} timeout={400}>
							<Box>
								{member.bio.slice(1).map((paragraph, idx) => (
									<ContentText key={idx} paragraph>
										{paragraph}
									</ContentText>
								))}
							</Box>
						</Collapse>

						<Button
							onClick={() => setExpanded(!expanded)}
							endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
							sx={{
								color: colors.primary.main,
								fontWeight: 700,
								fontSize: "0.78rem",
								letterSpacing: "0.1em",
								textTransform: "uppercase",
								fontFamily: "'Heebo', sans-serif",
								px: 0,
								mt: 0.5,
								borderRadius: 0,
								"&:hover": {
									backgroundColor: "transparent",
									color: colors.primary.dark,
									textDecoration: "underline",
								},
							}}
						>
							{expanded ? "Read Less" : "Read Full Bio"}
						</Button>
					</>
				)}
			</motion.div>
		</Grid>
	);

	return (
		<Grid
			container
			spacing={5}
			alignItems="flex-start"
			sx={{ mb: index < 1 ? 8 : 0 }}
		>
			{imageColumn}
			{textColumn}
		</Grid>
	);
};

export default TeamMember;
